import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import {
  Box, Typography, IconButton, Paper, Button, TextField, Tooltip, Skeleton,
  InputAdornment, Menu, MenuItem,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FitScreenIcon from '@mui/icons-material/FitScreen';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import { colors, typography, layout } from '../../../styles/theme';
import { getMyLots, getUserLotLists } from '../../../services/userLotsService';
import { useActiveSpace } from '../../context/ActiveSpaceContext';
import { useLocatedLot } from '../../hooks/useLocatedLot';
import { getScrollEdgeFadeSx, useScrollEdgeFade } from '../../../hooks/useScrollEdgeFade';
import { getMyCadParcels } from '../../../services/cadService';
import { useApiRecordCards } from '../../hooks/useApiRecordCards';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { lotRowMatchesSearch, parseSearchTerms, recordItemMatchesSearch } from '../../utils/recordSearch';
import { isRecordsApiSource } from '../../utils/recordsSource';
import ShareLotDialog from '../../../Components/ShareLotDialog';
import SearchableMultiSelect from '../../../Components/SearchableMultiSelect';
import PanelShell from '../PanelShell/PanelShell';
import PanelHeader, { panelCloseIconButtonSx } from '../PanelShell/PanelHeader';
import {
  emptyStateBoxSx,
  emptyStateIconBoxSx,
  emptyStateIconSx,
  emptyStateTitleSx,
  emptyStateHintSx,
  listFiltersBoxSx,
  listSearchFieldSx,
  listSearchIconSx,
  getListFilterChipSx,
  recordCountsRowSx,
  recordToolbarControlsSx,
  scrollableListZoneSx,
} from '../UserLotListsPanel/UserLotListsPanel.styles';
import {
  countLotsWithContent,
  formatRecordsSummary,
} from '../../utils/lotContentCounts';
import { dedupeLotsForMapHighlight } from '../../utils/enrichSharedLotsForMap';
import { resolveSpaceMirrorReadOwner, stripOwnerFavoritesFromLots } from '../../utils/spaceMirrorLots';
import {
  getDisplayUnits,
  normalizeLotsData,
} from '../../utils/userLotUnits';
import { countActiveParcelFilters } from '../../data/parcelFilterContract';
import { filterOwnedLotsByPanelFilters } from '../../utils/sharedListingFilters';
import {
  cadCollectionFilterValue,
  cadParcelsVisibleInRegistros,
  filterCadParcelsByCollectionFilters,
  isCadCollectionFilter,
  isCadRegistrosRow,
  mergeRegistrosRows,
  normalizeCadParcelsForRegistros,
  uniqueCadCollectionsFromParcels,
} from '../../../Showroom/utils/cadRegistros';
import RegistrosLotCard from './RegistrosLotCard';
import RecordCountsLabel from '../RecordCountsLabel';
import RegistrosCadParcelCard from './RegistrosCadParcelCard';
import RegistrosRecordCards from './RegistrosRecordCards';
import LoadingRecordCards from '../RecordCards/LoadingRecordCards';
import RecordViewModeToggle from '../RecordCards/RecordViewModeToggle';
import { useRecordViewMode } from '../../hooks/useRecordViewMode';
import {
  buildRecordCardItems,
  countRecordCardItems,
  getRecordCardLotIds,
} from '../../utils/recordCards';
import { onCommercialChanged } from '../../../Leads/commercialSummary';
import { CommercialFiltersAdornment, withCommercialAdornmentSx } from '../CommercialFilters/CommercialFiltersButton';
import { leadsSummaryClearButtonSx } from '../../../Leads/components/leadsUi.styles';
import {
  COMMERCIAL_SORT_OPTIONS,
  DEFAULT_COMMERCIAL_FILTERS,
  countActiveCommercialFilters,
  filterRecordsByCommercial,
  isCommercialSort,
  sortRecordsCommercial,
} from '../../../Leads/recordCommercialFilters';

const SIDEBAR_RAIL_WIDTH = layout.sidebarRailWidth;
const NO_LISTS_VALUE = '__NO_LISTS__';
const PANEL_BG = '#1c1c1c';
const PANEL_BORDER = '#4a4a4a';
const LIST_RENDER_CHUNK = 60;
const TEXT_SECONDARY = '#ababab';

const REGISTROS_PANEL_DESCRIPTION =
  'Accedé a lotes, unidades y marcadores que forman parte de tus registros.';

const getUnitListIds = (unit) => {
  const idsFromLists = Array.isArray(unit?.lists) ? unit.lists.map((x) => x?.id).filter(Boolean) : [];
  const idsFromListIds = Array.isArray(unit?.list_ids) ? unit.list_ids.filter(Boolean) : [];
  const legacyId = unit?.list_id ? [unit.list_id] : [];
  return Array.from(new Set([...idsFromLists, ...idsFromListIds, ...legacyId]));
};

const unitMatchesListFilters = (unit, sel) => {
  if (!sel.length) return true;
  const wantUnlisted = sel.includes(NO_LISTS_VALUE);
  const wantedIds = sel.filter((v) => v && v !== NO_LISTS_VALUE);
  const unitListIds = getUnitListIds(unit);
  const hasLegacyListNameOnly = !unitListIds.length && !!unit?.list_name;
  const isUnlisted = !unitListIds.length && !hasLegacyListNameOnly;
  const matchesAnyList = wantedIds.length ? wantedIds.some((id) => unitListIds.includes(id)) : false;
  if (wantUnlisted && wantedIds.length) return isUnlisted || matchesAnyList;
  if (wantUnlisted) return isUnlisted;
  return matchesAnyList;
};

const applyListFiltersToLots = (lots, sel) => {
  if (!sel.length) return lots;
  return lots
    .map((lot) => {
      const units = Array.isArray(lot.units) ? lot.units : [];
      if (!units.length) {
        // Marcador puro / lote sin unidades: solo si no pedimos listas concretas
        // (o si incluimos “sin lista”).
        if (sel.includes(NO_LISTS_VALUE) && sel.length === 1) return lot;
        return null;
      }
      const filteredUnits = units.filter((u) => unitMatchesListFilters(u, sel));
      if (!filteredUnits.length) return null;
      return { ...lot, units: filteredUnits, unit_count: filteredUnits.length };
    })
    .filter(Boolean);
};

/**
 * Solo órdenes comerciales. Default: Actividad reciente. El desempate interno
 * sigue siendo la fecha del registro (`latest_activity_at`, más reciente primero).
 */
const DEFAULT_REGISTROS_SORT = 'activity-desc';
const SORT_OPTIONS = COMMERCIAL_SORT_OPTIONS;
const SORT_LABELS = Object.fromEntries(SORT_OPTIONS.map((o) => [o.value, o.label]));

const REGISTROS_FILTER_CONTROL_HEIGHT = 40;

const getRegistrosFilterChipSx = (active) => ({
  ...getListFilterChipSx(active),
  height: REGISTROS_FILTER_CONTROL_HEIGHT,
  minHeight: REGISTROS_FILTER_CONTROL_HEIGHT,
  flexShrink: 0,
  whiteSpace: 'nowrap',
});

const registrosListFilterSx = {
  flex: 1,
  minWidth: 0,
  width: '100%',
};

const renderRegistrosListFilterValue = (selected, options) => {
  if (selected.length === 0) return 'Seleccionar listas';
  if (selected.length === 1) {
    return options.find((o) => o.value === selected[0])?.label ?? '1 lista';
  }
  return `${selected.length} listas`;
};

const registrosToolbarSkeletonSx = { bgcolor: '#383838', borderRadius: '4px' };

const registrosToolbarZoneSx = {
  flexShrink: 0,
  borderBottom: '1px solid rgba(255,255,255,0.06)',
};

// Una fila: resumen a la izquierda (con «…» si no entra) y orden + vista a la derecha.
const registrosCountsRowSx = recordCountsRowSx;

const registrosScrollZoneSx = {
  ...scrollableListZoneSx,
  mr: 0.5,
};

const LoadingRegistrosToolbar = () => (
  <Box sx={registrosToolbarZoneSx}>
    <Box sx={{ ...listFiltersBoxSx, px: 2.5, pt: 3, pb: 1.5 }}>
    <Skeleton variant="rounded" height={40} sx={registrosToolbarSkeletonSx} />
    <Box sx={{ display: 'flex', alignItems: 'stretch', gap: 1, width: '100%', mt: 0.5 }}>
      <Skeleton variant="rounded" width={72} height={REGISTROS_FILTER_CONTROL_HEIGHT} sx={registrosToolbarSkeletonSx} />
      <Skeleton variant="rounded" width={132} height={REGISTROS_FILTER_CONTROL_HEIGHT} sx={registrosToolbarSkeletonSx} />
      <Skeleton variant="rounded" height={REGISTROS_FILTER_CONTROL_HEIGHT} sx={{ ...registrosToolbarSkeletonSx, flex: 1 }} />
    </Box>
    <Box sx={registrosCountsRowSx}>
      <Skeleton variant="rounded" width={168} height={14} sx={registrosToolbarSkeletonSx} />
      <Box sx={recordToolbarControlsSx}>
        <Skeleton variant="rounded" width={148} height={14} sx={registrosToolbarSkeletonSx} />
        <Skeleton variant="rounded" width={80} height={28} sx={{ ...registrosToolbarSkeletonSx, borderRadius: '8px' }} />
      </Box>
    </Box>
    </Box>
  </Box>
);

const LoadingRegistrosCards = ({ count = 7 }) => {
  const cardSx = {
    borderRadius: '8px',
    border: 'none',
    backgroundColor: '#242424',
    px: '12px',
    py: '10px',
    minHeight: 96,
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    boxShadow: 'none',
  };

  const skPrimarySx = { bgcolor: '#383838', borderRadius: '4px' };
  const skMutedSx = { bgcolor: '#2E2E2E', borderRadius: '4px' };
  const skAccentSx = { bgcolor: 'rgba(109,168,167,0.24)', borderRadius: '4px' };
  const skActionSx = { bgcolor: '#383838' };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {Array.from({ length: count }).map((_, idx) => (
        <Box key={idx} sx={cardSx}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
            <Skeleton variant="rounded" width={20} height={20} sx={{ ...skMutedSx, flexShrink: 0, mt: '1px' }} />
            <Box sx={{ minWidth: 0, flex: 1, display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <Skeleton variant="rounded" width={`${68 - (idx % 4) * 6}%`} height={18} sx={skPrimarySx} />
              <Skeleton variant="rounded" width={`${44 - (idx % 3) * 5}%`} height={14} sx={skMutedSx} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {idx % 3 === 0 && (
                  <Skeleton variant="circular" width={6} height={6} sx={{ bgcolor: colors.primary }} />
                )}
                <Skeleton variant="rounded" width={idx % 2 === 0 ? 78 : 68} height={26} sx={skAccentSx} />
              </Box>
            </Box>
            <Skeleton variant="circular" width={24} height={24} sx={{ ...skActionSx, flexShrink: 0 }} />
          </Box>
        </Box>
      ))}
    </Box>
  );
};

const RegistrosEmptyState = () => (
  <Box sx={emptyStateBoxSx}>
    <Box sx={emptyStateIconBoxSx}>
      <PlaceOutlinedIcon sx={emptyStateIconSx} />
    </Box>
    <Typography sx={emptyStateTitleSx}>Sin unidades funcionales registradas</Typography>
    <Typography sx={emptyStateHintSx}>
      Seleccioná una unidad en el mapa y agregá tu información para verla acá.
    </Typography>
  </Box>
);

/* ─────────────────────────────────────────────
   RegistrosPanel
───────────────────────────────────────────── */
const RegistrosPanel = ({
  uiLeftInset = SIDEBAR_RAIL_WIDTH,
  onClose,
  onOpenLot,
  onLocateLot,
  onLocateCadParcel,
  onOpenCadParcel,
  onAddLot,
  onHighlightLotIdsChange,
  onHighlightLotsChange,
  onFitInView,
  hidden = false,
  parcelFilters = null,
  onWorkingSetChange,
}) => {
  const { activeOwnerUserId, isViewingSharedSpace } = useActiveSpace();
  // F3: compartir lote del owner en espacio ajeno; propio espacio → null.
  const shareOwnerUserId = resolveSpaceMirrorReadOwner({ isViewingSharedSpace, activeOwnerUserId });
  const [lots, setLots] = useState([]);
  const [cadParcels, setCadParcels] = useState([]);
  const [totalLots, setTotalLots] = useState(0);
  const [totalUnits, setTotalUnits] = useState(0);
  const [totalLotContent, setTotalLotContent] = useState(0);
  const [totalCadParcels, setTotalCadParcels] = useState(null);
  const [userLists, setUserLists] = useState([]);
  const [listFilters, setListFilters] = useState([]);
  const [markerFilter, setMarkerFilter] = useState('all'); // 'all' | 'favorites'
  // Marcadores son personales: en espacio ajeno no se filtra ni se pintan los del owner.
  useEffect(() => {
    if (isViewingSharedSpace) setMarkerFilter('all');
  }, [isViewingSharedSpace]);
  const [sortBy, setSortBy] = useState(DEFAULT_REGISTROS_SORT);
  const [commercialFilters, setCommercialFilters] = useState(DEFAULT_COMMERCIAL_FILTERS);
  const commercialFilterCount = countActiveCommercialFilters(commercialFilters);
  const [sortMenuAnchor, setSortMenuAnchor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingLists, setLoadingLists] = useState(true);
  const [search, setSearch] = useState('');
  const [expandedLotIds, setExpandedLotIds] = useState(() => new Set());
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareLot, setShareLot] = useState(null);
  const [mapPreviewActive, setMapPreviewActive] = useState(false);
  /** Lote ubicado desde la card: queda pintado y la card marcada, como una lista. */
  const {
    locatedLot,
    toggleLocatedLot: handleLocateLot,
    clearLocatedLot: forgetLocatedLot,
    isLotLocated,
    isRecordLocated,
  } = useLocatedLot({ onLocate: onLocateLot });
  // Vista por registro (default) / dos por fila / agrupada por lote; preferencia en localStorage.
  const {
    effectiveViewMode,
    setViewMode: setRecordViewMode,
    canUseGrid,
    isGrouped,
  } = useRecordViewMode();
  // Fade arriba / abajo en la lista (mismo uso que Historial).
  const { scrollRef, fadeTop, fadeBottom } = useScrollEdgeFade();
  const [visibleLotCount, setVisibleLotCount] = useState(LIST_RENDER_CHUNK);

  const fetchLotsAbortRef = useRef(null);
  const refreshListsAbortRef = useRef(null);
  const wasHiddenRef = useRef(hidden);

  // Espacio ajeno (F3): el invitado comparte el lote del owner (diálogo con
  // `ownerUserId`: UF, contenido, config y marca del owner).
  const handleShareLot = useCallback((lot) => {
    // Evita aria-hidden sobre un botón del menú que aún retiene foco.
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    setShareLot(lot || null);
    // Diferir apertura hasta que el Menu cierre y libere el foco.
    window.setTimeout(() => {
      setShareDialogOpen(true);
    }, 0);
  }, []);

  // Cambio de espacio: el diálogo de compartir es del espacio anterior.
  useEffect(() => {
    setShareDialogOpen(false);
    setShareLot(null);
  }, [activeOwnerUserId]);

  const toggleLotExpanded = (e, lotId) => {
    e?.stopPropagation?.();
    setExpandedLotIds((prev) => {
      const next = new Set(prev);
      if (next.has(lotId)) next.delete(lotId);
      else next.add(lotId);
      return next;
    });
  };

  const fetchLots = useCallback(async () => {
    fetchLotsAbortRef.current?.abort();
    const controller = new AbortController();
    fetchLotsAbortRef.current = controller;

    setLoading(true);
    try {
      const sel = Array.isArray(listFilters) ? listFilters : [];
      const cadastralWantedIds = sel.filter(
        (v) => v && v !== NO_LISTS_VALUE && !isCadCollectionFilter(v),
      );
      const singleListId = cadastralWantedIds.length === 1 && !sel.includes(NO_LISTS_VALUE)
        ? cadastralWantedIds[0]
        : null;
      const favoritesOnly = markerFilter === 'favorites' && !singleListId && sel.length === 0;

      const spaceOwnerId = activeOwnerUserId ? String(activeOwnerUserId).trim() : '';
      const [res, cadRes] = await Promise.all([
        getMyLots({
          listId: singleListId || undefined,
          favoritesOnly,
          includeFilterAttributes: true,
          includePrivateAttributes: true,
          includeCommercialSummary: true,
          includeLotContentSummary: true,
          signal: controller.signal,
          ...(spaceOwnerId ? { ownerUserId: spaceOwnerId } : {}),
        }),
        getMyCadParcels({
          include: 'commercial_summary',
          signal: controller.signal,
          ...(spaceOwnerId ? { ownerUserId: spaceOwnerId } : {}),
        }),
      ]);

      let all = normalizeLotsData(res?.data);
      // Espacio ajeno: los marcadores del owner son personales; no se muestran.
      let removedOwnerMarkers = 0;
      if (spaceOwnerId) {
        const stripped = stripOwnerFavoritesFromLots(all);
        all = stripped.lots;
        removedOwnerMarkers = stripped.removed;
      }
      const cadastralSel = sel.filter((v) => !isCadCollectionFilter(v));
      if (!singleListId && cadastralSel.length > 0) {
        all = applyListFiltersToLots(all, cadastralSel);
      } else if (singleListId) {
        // BE puede devolver unit_count total del lote; el badge debe reflejar units[] filtradas.
        all = all.map((lot) => {
          const units = getDisplayUnits(lot);
          return units.length ? { ...lot, unit_count: units.length } : lot;
        });
      } else if (sel.length > 0) {
        all = [];
      }
      if (markerFilter === 'favorites' && !favoritesOnly) {
        all = all.filter((l) => l.is_favorite === true);
      }

      setLots(all);
      setTotalLots(
        typeof res?.total === 'number'
          ? Math.max(0, res.total - removedOwnerMarkers)
          : all.length,
      );
      setTotalUnits(
        typeof res?.total_units === 'number'
          ? res.total_units
          : all.reduce((sum, l) => sum + (l.unit_count ?? getDisplayUnits(l).length), 0),
      );
      setTotalLotContent(
        typeof res?.total_lot_content === 'number'
          ? res.total_lot_content
          : countLotsWithContent(all),
      );

      if (cadRes?.ok) {
        const cadRows = normalizeCadParcelsForRegistros(cadRes.parcels);
        setCadParcels(cadRows);
        setTotalCadParcels(
          typeof cadRes.total === 'number' ? cadRes.total : cadRows.length,
        );
      } else {
        if (cadRes?.message) {
          console.error('Error fetching registros CAD:', cadRes.message);
        }
        setCadParcels([]);
        setTotalCadParcels(null);
      }
    } catch (e) {
      if (e?.name === 'AbortError') return;
      console.error('Error fetching registros:', e);
      setLots([]);
      setCadParcels([]);
      setTotalLots(0);
      setTotalUnits(0);
      setTotalLotContent(0);
      setTotalCadParcels(null);
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, [listFilters, markerFilter, activeOwnerUserId]);

  // Resumen comercial: al cambiar una etapa de interés o marcar consultas
  // vistas, se vuelve a pedir (sin optimista: el status lo calcula la API).
  useEffect(() => {
    let timer = null;
    const off = onCommercialChanged(() => {
      clearTimeout(timer);
      timer = setTimeout(() => fetchLots(), 400);
    });
    return () => { clearTimeout(timer); off(); };
  }, [fetchLots]);

  useEffect(() => {
    fetchLots();
    return () => fetchLotsAbortRef.current?.abort();
  }, [fetchLots]);

  // Al volver desde el dock (panel estaba hidden), refrescar por si cambió
  // marcador, unidades funcionales u otros datos del lote.
  useEffect(() => {
    const wasHidden = wasHiddenRef.current;
    wasHiddenRef.current = hidden;
    if (wasHidden && !hidden) {
      fetchLots();
    }
  }, [hidden, fetchLots]);

  const refreshUserLists = useCallback(async () => {
    refreshListsAbortRef.current?.abort();
    const controller = new AbortController();
    refreshListsAbortRef.current = controller;

    setLoadingLists(true);
    try {
      const spaceOwnerId = activeOwnerUserId ? String(activeOwnerUserId).trim() : '';
      const res = await getUserLotLists({
        signal: controller.signal,
        ...(spaceOwnerId ? { ownerUserId: spaceOwnerId } : {}),
      });
      setUserLists(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      if (e?.name === 'AbortError') return;
      console.error('Error fetching user lot lists:', e);
      setUserLists([]);
    } finally {
      if (!controller.signal.aborted) setLoadingLists(false);
    }
  }, [activeOwnerUserId]);

  useEffect(() => {
    refreshUserLists();
    return () => refreshListsAbortRef.current?.abort();
  }, [refreshUserLists]);

  const parcelFilterCount = countActiveParcelFilters(parcelFilters || {});
  const hasParcelFilters = parcelFilterCount > 0;
  const showCadParcels = cadParcelsVisibleInRegistros({
    markerFilter,
    listFilters,
    hasParcelFilters,
  });

  // Universo que el panel está mostrando (lista / marcadores / búsqueda).
  // Los filtros de parcelas recortan este conjunto, no los 954 registros.
  // Buscador (`utils/recordSearch`): normaliza acentos, todas las palabras (AND).
  // Agrupada: el lote queda si matchea él o alguno de sus registros. Por registro:
  // no se filtran lotes acá; se filtra cada card (con los datos de getmyrecords
  // que ya llegaron), así matchear una UF muestra esa UF y no todas las del lote.
  const debouncedSearch = useDebouncedValue(search);
  const searchTerms = useMemo(() => parseSearchTerms(debouncedSearch), [debouncedSearch]);
  const hasSearch = searchTerms.length > 0;

  const toolbarLots = useMemo(() => {
    if (!hasSearch || !isGrouped) return lots;
    return lots.filter((l) => lotRowMatchesSearch(l, searchTerms));
  }, [lots, hasSearch, isGrouped, searchTerms]);

  const toolbarCadParcels = useMemo(() => {
    if (!showCadParcels) return [];
    const byCollection = filterCadParcelsByCollectionFilters(cadParcels, listFilters);
    if (!hasSearch || !isGrouped) return byCollection;
    return byCollection.filter((row) => lotRowMatchesSearch(row, searchTerms));
  }, [cadParcels, hasSearch, isGrouped, searchTerms, showCadParcels, listFilters]);

  // Base por fecha del registro: el orden comercial desempata con ella (estable).
  const mergedRows = useMemo(() => {
    const cadastralRows = hasParcelFilters
      ? filterOwnedLotsByPanelFilters(toolbarLots, parcelFilters)
      : toolbarLots;
    return mergeRegistrosRows(cadastralRows, toolbarCadParcels, 'recent');
  }, [toolbarLots, toolbarCadParcels, hasParcelFilters, parcelFilters]);

  /** Vista agrupada: filtros y orden comerciales por lote (rollup lote + UF). */
  const filtered = useMemo(() => {
    if (!isGrouped) return [];
    const byCommercial = filterRecordsByCommercial(mergedRows, commercialFilters);
    return isCommercialSort(sortBy) ? sortRecordsCommercial(byCommercial, sortBy) : byCommercial;
  }, [isGrouped, mergedRows, sortBy, commercialFilters]);

  // Fuente `api` (recordsSource): las cards toman los datos de getmyrecords;
  // universo, filtros y acciones siguen siendo los de la carga legacy.
  const { cards: apiRecordCards } = useApiRecordCards({
    enabled: isRecordsApiSource() && !isGrouped,
    ready: !loading,
    ownerUserId: activeOwnerUserId || null,
    includeCad: showCadParcels,
    // Con filtros de lista / marcadores, getmylots trae un subconjunto: la cantidad
    // no indica altas ni bajas y getmyrecords ya tiene todo (no se vuelve a pedir).
    reloadKey: listFilters.length > 0 || markerFilter !== 'all'
      ? null
      : `${lots.length}:${cadParcels.length}`,
  });

  /** Vista por registro: búsqueda por card (UF / lote / CAD), con los datos de la card. */
  const searchedRecordItems = useMemo(() => {
    if (isGrouped) return [];
    const items = buildRecordCardItems(mergedRows, { apiCards: apiRecordCards });
    return hasSearch ? items.filter((item) => recordItemMatchesSearch(item, searchTerms)) : items;
  }, [isGrouped, mergedRows, apiRecordCards, hasSearch, searchTerms]);

  /** Vista por registro: filtros y orden comerciales por card (resumen de esa UF / lote / CAD). */
  const recordItems = useMemo(() => {
    if (isGrouped) return [];
    const byCommercial = filterRecordsByCommercial(searchedRecordItems, commercialFilters);
    return isCommercialSort(sortBy) ? sortRecordsCommercial(byCommercial, sortBy) : byCommercial;
  }, [isGrouped, searchedRecordItems, sortBy, commercialFilters]);

  // Conjunto de trabajo del mapa: con búsqueda en la vista por registro, los lotes
  // de las cards que matchean (en agrupada ya viene filtrado en `toolbarLots`).
  const workingSetLots = useMemo(() => {
    if (isGrouped || !hasSearch) return toolbarLots;
    const lotIds = getRecordCardLotIds(searchedRecordItems);
    return lots.filter((lot) => lotIds.has(String(lot.lot_id)));
  }, [isGrouped, hasSearch, toolbarLots, searchedRecordItems, lots]);
  const shownCount = isGrouped ? filtered.length : recordItems.length;

  useEffect(() => {
    if (loading && lots.length === 0) return undefined;
    onWorkingSetChange?.(workingSetLots);
    return undefined;
  }, [loading, lots.length, workingSetLots, onWorkingSetChange]);

  useEffect(() => () => onWorkingSetChange?.(null), [onWorkingSetChange]);

  // Lotes catastrales que se pintan en el mapa: los de las cards visibles tras filtros.
  const cadastralFiltered = useMemo(() => {
    if (isGrouped) return filtered.filter((row) => !isCadRegistrosRow(row));
    const lotIds = getRecordCardLotIds(recordItems);
    return mergedRows.filter((row) => !isCadRegistrosRow(row) && lotIds.has(String(row.lot_id)));
  }, [isGrouped, filtered, recordItems, mergedRows]);
  const recordCounts = useMemo(
    () => (isGrouped ? null : countRecordCardItems(recordItems)),
    [isGrouped, recordItems],
  );

  const hasLocalListFilter = hasSearch
    || listFilters.length > 0
    || markerFilter === 'favorites'
    || hasParcelFilters
    || commercialFilterCount > 0;
  const cadastralLotCount = hasLocalListFilter
    ? cadastralFiltered.length
    : totalLots;
  const cadLotCount = showCadParcels
    ? (commercialFilterCount > 0
      ? filtered.filter(isCadRegistrosRow).length
      : (hasLocalListFilter ? toolbarCadParcels.length : (totalCadParcels || 0)))
    : 0;
  // Por registro: los totales cuentan las cards que se ven (los marcadores sin
  // contenido no son registros). Agrupada: totales de la API o del filtro local.
  const displayLotCount = recordCounts ? recordCounts.lots : cadastralLotCount + cadLotCount;
  const displayUnitCount = recordCounts
    ? recordCounts.units
    : (hasLocalListFilter
      ? cadastralFiltered.reduce((sum, l) => sum + (l.unit_count ?? getDisplayUnits(l).length), 0)
      : totalUnits);
  const displayLotContentCount = recordCounts
    ? recordCounts.lotContent
    : (hasLocalListFilter ? countLotsWithContent(cadastralFiltered) : totalLotContent);

  // «2128 lotes · 941 registros» (UF + contenido); los CAD van como lotes.
  const countsSummary = formatRecordsSummary(displayLotCount, displayUnitCount, displayLotContentCount);

  useEffect(() => {
    setVisibleLotCount(LIST_RENDER_CHUNK);
  }, [search, listFilters, markerFilter, sortBy, parcelFilters, commercialFilters, effectiveViewMode]);

  const visibleLots = useMemo(
    () => filtered.slice(0, visibleLotCount),
    [filtered, visibleLotCount],
  );
  const visibleRecordItems = useMemo(
    () => recordItems.slice(0, visibleLotCount),
    [recordItems, visibleLotCount],
  );
  const visibleShownCount = isGrouped ? visibleLots.length : visibleRecordItems.length;

  const filteredLotCountRef = useRef(0);
  filteredLotCountRef.current = shownCount;

  const handleListScroll = useCallback((e) => {
    const el = e.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight > 400) return;
    setVisibleLotCount((count) => (
      count >= filteredLotCountRef.current ? count : count + LIST_RENDER_CHUNK
    ));
  }, []);

  const emitMapHighlight = useCallback((rows, active) => {
    if (!active) {
      onHighlightLotIdsChange?.([]);
      onHighlightLotsChange?.([]);
      return;
    }
    const deduped = dedupeLotsForMapHighlight(rows);
    onHighlightLotIdsChange?.(deduped.map((l) => l.lot_id).filter(Boolean));
    onHighlightLotsChange?.(deduped);
  }, [onHighlightLotIdsChange, onHighlightLotsChange]);

  useEffect(() => {
    const showOnMap = hasParcelFilters || mapPreviewActive;
    if (showOnMap) {
      const rows = locatedLot?.lot_id
        ? [...cadastralFiltered, locatedLot]
        : cadastralFiltered;
      emitMapHighlight(rows, true);
      return;
    }
    if (locatedLot?.lot_id) {
      emitMapHighlight([locatedLot], true);
      return;
    }
    emitMapHighlight([], false);
  }, [cadastralFiltered, mapPreviewActive, hasParcelFilters, locatedLot, emitMapHighlight]);

  useEffect(() => () => {
    emitMapHighlight([], false);
  }, [emitMapHighlight]);

  const toggleMapPreview = useCallback(() => {
    const next = !mapPreviewActive;
    if (next) forgetLocatedLot();
    setMapPreviewActive(next);
    if (next) {
      emitMapHighlight(cadastralFiltered, true);
      onFitInView?.();
    } else {
      emitMapHighlight([], false);
    }
  }, [mapPreviewActive, cadastralFiltered, emitMapHighlight, onFitInView, forgetLocatedLot]);

  // `options.openLotContent`: abrir la ficha en el contenido del lote (card de contenido).
  const handleOpenLot = useCallback((lotId, options) => {
    forgetLocatedLot();
    onOpenLot?.(lotId, options);
  }, [forgetLocatedLot, onOpenLot]);

  const handleOpenCadParcel = useCallback((parcel) => {
    forgetLocatedLot();
    onOpenCadParcel?.(parcel);
  }, [forgetLocatedLot, onOpenCadParcel]);

  const handleOpenUnit = useCallback((lot, unit) => {
    const userLotId = unit?.id || unit?.user_lot_id;
    if (!userLotId) return;
    forgetLocatedLot();
    if (onAddLot) onAddLot(lot.lot_id, userLotId);
    else onOpenLot?.(lot.lot_id);
  }, [forgetLocatedLot, onAddLot, onOpenLot]);

  const listFilterOptions = useMemo(
    () => {
      const collections = [
        ...userLists.map((row) => ({ value: row.id, label: row.name || 'Lista' })),
        ...uniqueCadCollectionsFromParcels(cadParcels).map((row) => ({
          value: cadCollectionFilterValue(row.slug),
          label: row.name,
        })),
      ].sort((a, b) => a.label.localeCompare(b.label, 'es', { sensitivity: 'base' }));
      return [
        { value: NO_LISTS_VALUE, label: 'Sin lista asignada' },
        ...collections,
      ];
    },
    [userLists, cadParcels],
  );

  const isUniverseEmpty = !loading
    && lots.length === 0
    && cadParcels.length === 0
    && markerFilter === 'all'
    && listFilters.length === 0
    && !search.trim();

  const hasActiveToolbarState = markerFilter !== 'all'
    || listFilters.length > 0
    || Boolean(search.trim())
    || hasParcelFilters
    || commercialFilterCount > 0;
  const hasNonSearchFilters = markerFilter !== 'all'
    || listFilters.length > 0
    || hasParcelFilters
    || commercialFilterCount > 0;
  const mapShowsFilteredLots = mapPreviewActive || hasParcelFilters;
  const showToolbar = !isUniverseEmpty;
  const showToolbarSkeleton = loading && lots.length === 0 && !hasActiveToolbarState;
  const showListMembership = listFilters.length > 1;

  return (
    <PanelShell uiLeftInset={uiLeftInset} onClose={onClose} hidden={hidden}>
      {({ snap }) => (
        <Paper
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            bgcolor: PANEL_BG,
            color: colors.textPrimary,
            boxShadow: '0 18px 45px rgba(15,23,42,0.55)',
            border: `1px solid ${PANEL_BORDER}`,
            borderRadius: { xs: 0, sm: '16px' },
          }}
        >
          <Box sx={{ bgcolor: PANEL_BG }}>
            <PanelHeader
              title="REGISTROS"
              subtitle={REGISTROS_PANEL_DESCRIPTION}
              actions={(
                <>
                  {!hasParcelFilters && cadastralFiltered.length > 0 && (
                    <Tooltip
                      title={mapPreviewActive ? 'Ocultar en el mapa' : 'Mostrar en el mapa'}
                      placement="bottom"
                      arrow
                    >
                      <IconButton
                        onClick={toggleMapPreview}
                        size="small"
                        aria-label={mapPreviewActive ? 'Ocultar en el mapa' : 'Mostrar en el mapa'}
                        sx={{
                          color: mapPreviewActive ? colors.primary : '#E8F4F4',
                          '&:hover': { color: '#FFFFFF', bgcolor: 'rgba(255,255,255,0.06)' },
                        }}
                      >
                        {mapPreviewActive ? (
                          <VisibilityOffOutlinedIcon fontSize="small" />
                        ) : (
                          <VisibilityOutlinedIcon fontSize="small" />
                        )}
                      </IconButton>
                    </Tooltip>
                  )}
                  {onFitInView && mapShowsFilteredLots && cadastralFiltered.length > 0 && (
                    <Tooltip title="Encuadrar en el mapa" placement="bottom" arrow>
                      <IconButton
                        onClick={onFitInView}
                        size="small"
                        aria-label="Encuadrar en el mapa"
                        sx={{
                          color: '#E8F4F4',
                          '&:hover': { color: '#FFFFFF', bgcolor: 'rgba(255,255,255,0.06)' },
                        }}
                      >
                        <FitScreenIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  <IconButton onClick={onClose} size="small" aria-label="Cerrar panel" sx={panelCloseIconButtonSx}>
                    <CloseIcon />
                  </IconButton>
                </>
              )}
            />
          </Box>

          {(snap === 'expanded' || snap === undefined) && showToolbar && (
            showToolbarSkeleton ? (
              <LoadingRegistrosToolbar />
            ) : (
            <Box sx={registrosToolbarZoneSx}>
            <Box sx={{ ...listFiltersBoxSx, px: 2.5, pt: 3, pb: 1.5 }}>
              <TextField
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar por título, dirección o barrio..."
                fullWidth
                size="small"
                variant="outlined"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={listSearchIconSx} />
                    </InputAdornment>
                  ),
                  // Filtros comerciales dentro del buscador, como en Leads.
                  endAdornment: (
                    <CommercialFiltersAdornment value={commercialFilters} onChange={setCommercialFilters} />
                  ),
                }}
                sx={withCommercialAdornmentSx(listSearchFieldSx)}
              />

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'stretch',
                  gap: 1,
                  width: '100%',
                  mt: 0.5,
                }}
              >
                <Button
                  size="small"
                  onClick={() => setMarkerFilter('all')}
                  sx={getRegistrosFilterChipSx(markerFilter === 'all')}
                >
                  Todos
                </Button>
                {!isViewingSharedSpace && (
                  <Button
                    size="small"
                    onClick={() => setMarkerFilter('favorites')}
                    startIcon={(
                      <Box
                        sx={{
                          width: 6,
                          height: 6,
                          borderRadius: '50%',
                          bgcolor: colors.primary,
                          flexShrink: 0,
                        }}
                      />
                    )}
                    sx={getRegistrosFilterChipSx(markerFilter === 'favorites')}
                  >
                    Con marcadores
                  </Button>
                )}
                <SearchableMultiSelect
                  id="registros-list-filter"
                  options={listFilterOptions}
                  value={listFilters}
                  onChange={setListFilters}
                  placeholder="Seleccionar listas"
                  searchPlaceholder="Buscar lista..."
                  disabled={loadingLists}
                  loading={loadingLists}
                  controlHeight={REGISTROS_FILTER_CONTROL_HEIGHT}
                  menuMaxHeight={240}
                  sx={registrosListFilterSx}
                  renderValue={renderRegistrosListFilterValue}
                  emptySearchMessage="Sin listas que coincidan"
                />
              </Box>

              <Box sx={registrosCountsRowSx}>
                {loading && lots.length === 0 ? (
                  <>
                    <Skeleton variant="rounded" width={168} height={14} sx={registrosToolbarSkeletonSx} />
                    <Box sx={recordToolbarControlsSx}>
                      <Skeleton variant="rounded" width={148} height={14} sx={registrosToolbarSkeletonSx} />
                      <Skeleton variant="rounded" width={80} height={28} sx={{ ...registrosToolbarSkeletonSx, borderRadius: '8px' }} />
                    </Box>
                  </>
                ) : (
                  <>
                    <RecordCountsLabel tooltip={countsSummary.tooltip}>
                      {countsSummary.label}
                    </RecordCountsLabel>
                    <Box sx={recordToolbarControlsSx}>
                    <Button
                      size="small"
                      onClick={(e) => setSortMenuAnchor(e.currentTarget)}
                      endIcon={<ExpandMoreIcon sx={{ fontSize: '16px !important' }} />}
                      sx={{
                        textTransform: 'none',
                        color: TEXT_SECONDARY,
                        fontSize: '12px',
                        fontWeight: 400,
                        minHeight: 24,
                        px: 0.5,
                        flexShrink: 0,
                        whiteSpace: 'nowrap',
                        '&:hover': { bgcolor: 'rgba(255,255,255,0.04)', color: '#e5e7eb' },
                        '& .MuiButton-endIcon': { ml: 0.25 },
                      }}
                    >
                      Ordenar por:{' '}
                      <Box component="span" sx={{ color: '#e5e7eb', fontWeight: 500, ml: 0.5 }}>
                        {SORT_LABELS[sortBy] || SORT_LABELS[DEFAULT_REGISTROS_SORT]}
                      </Box>
                    </Button>
                    <RecordViewModeToggle
                      value={effectiveViewMode}
                      onChange={setRecordViewMode}
                      canUseGrid={canUseGrid}
                    />
                    </Box>
                  </>
                )}
                <Menu
                  anchorEl={sortMenuAnchor}
                  open={Boolean(sortMenuAnchor)}
                  onClose={() => setSortMenuAnchor(null)}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                  PaperProps={{
                    sx: {
                      bgcolor: '#1d1d1d',
                      border: '1px solid rgba(255,255,255,0.08)',
                      '& .MuiMenuItem-root': { color: '#ffffff', fontSize: '13px' },
                    },
                  }}
                >
                  {SORT_OPTIONS.map((option) => (
                    <MenuItem
                      key={option.value}
                      selected={sortBy === option.value}
                      onClick={() => {
                        setSortBy(option.value);
                        setSortMenuAnchor(null);
                      }}
                    >
                      {option.label}
                    </MenuItem>
                  ))}
                </Menu>
              </Box>
            </Box>
            </Box>
            )
          )}

          <Box
            ref={scrollRef}
            onScroll={handleListScroll}
            data-testid="registros-scroll"
            sx={{
              ...registrosScrollZoneSx,
              px: 2.5,
              pt: showToolbar && (loading || shownCount > 0) ? 1.5 : 0,
              display: 'flex',
              flexDirection: 'column',
              ...getScrollEdgeFadeSx(fadeTop, fadeBottom),
            }}
          >
            {loading ? (
              isGrouped
                ? <LoadingRegistrosCards count={7} />
                : <LoadingRecordCards count={effectiveViewMode === 'grid' ? 6 : 5} layout={effectiveViewMode} />
            ) : isUniverseEmpty ? (
              <RegistrosEmptyState />
            ) : shownCount === 0 ? (
              <Box sx={{ ...emptyStateBoxSx, minHeight: 0, py: 0 }}>
                <Typography sx={emptyStateTitleSx}>No hay resultados que coincidan</Typography>
                <Typography sx={emptyStateHintSx}>
                  {hasSearch
                    ? `Ningún registro coincide con «${debouncedSearch.trim()}». Probá con otro título, dirección o barrio${hasNonSearchFilters ? ' o limpiá los filtros' : ''}.`
                    : hasParcelFilters
                      ? 'No hay registros que coincidan con los filtros aplicados.'
                      : 'Probá otra búsqueda o limpiá los filtros aplicados.'}
                </Typography>
                {commercialFilterCount > 0 ? (
                  <Button
                    size="small"
                    onClick={() => setCommercialFilters(DEFAULT_COMMERCIAL_FILTERS)}
                    sx={leadsSummaryClearButtonSx}
                  >
                    Limpiar filtros comerciales
                  </Button>
                ) : null}
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {!isGrouped ? (
                  <RegistrosRecordCards
                    items={visibleRecordItems}
                    layout={effectiveViewMode}
                    isRecordLocated={(item) => isRecordLocated(item.key)}
                    onLocateLot={handleLocateLot}
                    onLocateCadParcel={onLocateCadParcel}
                    onOpenLot={handleOpenLot}
                    onOpenUnit={handleOpenUnit}
                    onOpenCadParcel={handleOpenCadParcel}
                    onShareLot={handleShareLot}
                    showListMembership={showListMembership}
                    listFilters={listFilters}
                    userLists={userLists}
                    noListsValue={NO_LISTS_VALUE}
                  />
                ) : null}
                {isGrouped && visibleLots.map((row) => (
                  isCadRegistrosRow(row) ? (
                    <RegistrosCadParcelCard
                      key={`cad:${row.id}`}
                      parcel={row}
                      onLocate={onLocateCadParcel}
                      onOpen={handleOpenCadParcel}
                    />
                  ) : (
                    <RegistrosLotCard
                      key={`lot:${row.lot_id}`}
                      lot={row}
                      isExpanded={expandedLotIds.has(row.lot_id)}
                      onToggleExpand={toggleLotExpanded}
                      onOpenLot={handleOpenLot}
                      onOpenUnit={handleOpenUnit}
                      onLocateLot={handleLocateLot}
                      locateActive={isLotLocated(row.lot_id)}
                      onShareLot={handleShareLot}
                      showListMembership={showListMembership}
                      listFilters={listFilters}
                      userLists={userLists}
                      noListsValue={NO_LISTS_VALUE}
                    />
                  )
                ))}
                {shownCount > visibleShownCount && (
                  <Typography
                    sx={{
                      textAlign: 'center',
                      py: 1,
                      fontSize: '11.5px',
                      color: 'rgba(156,163,175,0.75)',
                      fontFamily: typography.fontFamily.primary,
                    }}
                  >
                    Mostrando {visibleShownCount} de {shownCount} · seguí desplazándote para ver más
                  </Typography>
                )}
              </Box>
            )}
          </Box>

          <ShareLotDialog
            key={shareOwnerUserId || 'own-space'}
            open={shareDialogOpen}
            ownerUserId={shareOwnerUserId}
            lotId={shareLot?.lot_id || ''}
            lotName={shareLot?.lot_name || shareLot?.lot_id || ''}
            provinceName={shareLot?.province_name || ''}
            departmentName={shareLot?.department_name || ''}
            initialUnits={shareLot ? getDisplayUnits(shareLot) : null}
            onClose={() => {
              setShareDialogOpen(false);
              setShareLot(null);
            }}
          />
        </Paper>
      )}
    </PanelShell>
  );
};

export default RegistrosPanel;
